'use server'

import { apiRequest, ApiResponse } from '../request'

export type QuickMessageVisibility = 'PRIVATE' | 'COMPANY' | 'ORGANIZATION'

export interface QuickMessage {
    id: number
    trigger: string
    title: string
    body: string
    mediaUrls: string[] | null
    visibility: QuickMessageVisibility
    organizationId: number
    companyId: number | null
    departmentId: number | null
    department?: { id: number; name: string } | null
    createdById: number
    createdBy?: { id: number; name: string } | null
    usageCount: number
    active: boolean
    createdAt: string
    updatedAt: string
}

export interface CreateQuickMessageData {
    trigger: string
    title: string
    body: string
    mediaUrls?: string[]
    visibility?: QuickMessageVisibility
    departmentId?: number
    active?: boolean
}

export type UpdateQuickMessageData = Partial<CreateQuickMessageData>

export interface QuickMessageFilters {
    active?: boolean
    visibility?: QuickMessageVisibility
    departmentId?: number
    search?: string
}

export const QUICK_MESSAGE_VISIBILITY_LABELS: Record<
    QuickMessageVisibility,
    string
> = {
    PRIVATE: 'Somente eu',
    COMPANY: 'Minha unidade',
    ORGANIZATION: 'Toda a rede',
}

export const QUICK_MESSAGE_VISIBILITY_DESCRIPTIONS: Record<
    QuickMessageVisibility,
    string
> = {
    PRIVATE: 'Só você vê este atalho no chat.',
    COMPANY: 'Todos os usuários desta unidade veem o atalho.',
    ORGANIZATION: 'Todas as unidades da rede veem o atalho.',
}

export const QUICK_MESSAGE_VISIBILITY_COLORS: Record<
    QuickMessageVisibility,
    string
> = {
    PRIVATE: 'badge-ghost',
    COMPANY: 'badge-info',
    ORGANIZATION: 'badge-secondary',
}

export async function getQuickMessages(
    filters?: QuickMessageFilters
): Promise<ApiResponse<QuickMessage[]>> {
    return apiRequest<QuickMessage[]>('messaging/quick-messages', {
        params: filters as any,
    })
}

/** O que o autocomplete do chat mostra (respeita visibilidade e setor). */
export async function getAvailableQuickMessages(
    departmentId?: number | null
): Promise<ApiResponse<QuickMessage[]>> {
    return apiRequest<QuickMessage[]>('messaging/quick-messages/available', {
        params: departmentId != null ? { departmentId } : undefined,
    })
}

export async function getQuickMessage(
    id: number
): Promise<ApiResponse<QuickMessage>> {
    return apiRequest<QuickMessage>(`messaging/quick-messages/${id}`, {})
}

export async function createQuickMessage(
    data: CreateQuickMessageData
): Promise<ApiResponse<QuickMessage>> {
    return apiRequest<QuickMessage>('messaging/quick-messages', { data }, 'post')
}

export async function updateQuickMessage(
    id: number,
    data: UpdateQuickMessageData
): Promise<ApiResponse<QuickMessage>> {
    return apiRequest<QuickMessage>(
        `messaging/quick-messages/${id}`,
        { data },
        'patch'
    )
}

export async function deleteQuickMessage(
    id: number
): Promise<ApiResponse<{ success: boolean }>> {
    return apiRequest<{ success: boolean }>(
        `messaging/quick-messages/${id}`,
        {},
        'delete'
    )
}

/**
 * Resolve as variáveis no contexto do atendimento.
 *
 * A renderização é do backend de propósito: cada variável tem allowlist
 * explícita para não vazar campo sensível, e refazer isso no front duplicaria a
 * regra de segurança.
 */
export async function renderQuickMessage(
    id: number,
    chatId: number
): Promise<ApiResponse<{ body: string; mediaUrls: string[] }>> {
    return apiRequest<{ body: string; mediaUrls: string[] }>(
        `messaging/quick-messages/${id}/render`,
        { data: { chatId } },
        'post'
    )
}
